import React, { useEffect, useRef, useState } from 'react'
import moment from 'moment'
// XCircle は lucide の circle-x アイコン（このバージョンでは旧名 XCircle で export）
import { ArrowDown, ArrowUp, Paperclip, Trash2, XCircle } from 'lucide-react'

import A from 'components/A'
import { ComboBox } from 'components/ComboBox'
import type { ComboBoxOption, ComboBoxChange } from 'components/ComboBox'
import { SpConfirmModal } from 'components/SpConfirmModal'
import type { SnackbarPayload } from 'components/Snackbar/useSnackbar'
import type {
  DeliveryWork,
  DeliveryWorkAttachment,
} from 'repositories/ShipmentApi/getShipment/type'
import { canPreviewFileAttachment } from 'utils/commonFunctions'
import type { DeliveryWorkTypeSetting } from 'repositories/ShipmentApi/getDeliveryWorkTypeSettings/type'
import { DELIVERY_DESTINATION_PLACEHOLDER } from '../utils/deliveryWorkMappers'
import type { VisitStamps, SavedDeliverySource } from '../utils/deliveryWorkMappers'
import { useDeliveryWorkForm, ACCEPT_ATTR } from '../hooks/useDeliveryWorkForm'

import { DeliveryWorkTypeChips } from './DeliveryWorkTypeChips'
import { buildAttachmentPreviewUrl } from './attachmentPreviewUrl'
import styles from './ShipmentVisitBlock.module.css'

type Tab = 'stamp' | 'detail'

type StampType = 'checkin' | 'start' | 'end' | 'checkout'

const STAMP_LABELS: Record<StampType, string> = {
  checkin: '入場',
  start: '作業開始',
  end: '作業完了',
  checkout: '退場',
}

// 納品先名称（手入力）の最大文字数。shipment_visits.delivery_destination = VARCHAR(50) / BE @Size(max=50)
const DELIVERY_DESTINATION_MAX_LENGTH = 50

const formatStampTime = (value: string): string =>
  moment(value, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD HH:mm:ss')

type Props = {
  visitId: string
  visitNumber: number
  shipmentUid: string
  dbVisitId: number | null
  deliveryReportUid: string
  driverPhone: string
  deliveryWork: DeliveryWork | null
  deliveryWorkAttachments: DeliveryWorkAttachment[]
  workTypeSettings: DeliveryWorkTypeSetting[]
  notify: (payload: SnackbarPayload) => void
  onSaved: (savedVisitId: number) => Promise<void>
  onDirtyChange: (visitId: string, dirty: boolean) => void
  deliveryDestinationOptions: ComboBoxOption[]
  deliveryHistoryValues: ReadonlySet<string>
  deliveryTenantLabel: string
  savedDeliveryLabel: string
  savedDeliverySource: SavedDeliverySource
  stamps: VisitStamps
  isCompleted: boolean
  isStamping: boolean
  isMutatingVisit: boolean
  showControls: boolean
  canMoveUp: boolean
  canMoveDown: boolean
  onDeliveryTenantChange: (next: ComboBoxChange) => void
  onSelectDelivery: () => void
  onClearDelivery: () => void
  onStampClick: (type: 'checkin' | 'start' | 'end' | 'checkout') => void | Promise<void>
  onDelete: () => void
  onMoveUp: () => void
  onMoveDown: () => void
}

export const ShipmentVisitBlock: React.FC<Props> = ({
  visitId,
  visitNumber,
  shipmentUid,
  dbVisitId,
  deliveryReportUid,
  driverPhone,
  deliveryWork,
  deliveryWorkAttachments,
  workTypeSettings,
  notify,
  onSaved,
  onDirtyChange,
  deliveryDestinationOptions,
  deliveryHistoryValues,
  deliveryTenantLabel,
  savedDeliveryLabel,
  savedDeliverySource,
  stamps,
  isCompleted,
  isStamping,
  isMutatingVisit,
  showControls,
  canMoveUp,
  canMoveDown,
  onDeliveryTenantChange,
  onSelectDelivery,
  onClearDelivery,
  onStampClick,
  onDelete,
  onMoveUp,
  onMoveDown,
}) => {
  const [activeTab, setActiveTab] = useState<Tab>('stamp')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showSaveConfirm, setShowSaveConfirm] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [stampConfirm, setStampConfirm] = useState<StampType | null>(null)
  const [isStampConfirmSubmitting, setIsStampConfirmSubmitting] = useState(false)

  const {
    selectedIds,
    toggleId,
    memo,
    setMemo,
    existingAttachments,
    newFiles,
    isSaving,
    isPersisted,
    isDirty,
    handleAddFile,
    handleRemoveExistingAttachment,
    handleRemoveNewFile,
    handleDownloadAttachment,
    handleSave,
  } = useDeliveryWorkForm({
    shipmentUid,
    dbVisitId,
    deliveryReportUid,
    driverPhone,
    initialDeliveryWork: deliveryWork,
    initialAttachments: deliveryWorkAttachments,
    notify,
    onSaved,
  })

  // 保存不可（配送完了 or 未永続 visit）の場合は作業詳細を編集できない
  const isDetailDisabled = isCompleted || !isPersisted
  // 保存中は多重送信防止のため入力も含めて非活性にする
  const isInputDisabled = isDetailDisabled || isSaving

  useEffect(() => {
    if (activeTab !== 'detail') return
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [memo, activeTab])

  // 未保存状態を親へ報告（ページ離脱ガード用）。アンマウント時は未保存を解除する
  useEffect(() => {
    onDirtyChange(visitId, isDirty)
    return () => onDirtyChange(visitId, false)
  }, [visitId, isDirty, onDirtyChange])

  const handleFileAdd = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleAddFile(file)
    }
    e.target.value = ''
  }

  const handleStampButtonClick = (type: StampType) => {
    setIsStampConfirmSubmitting(false)
    setStampConfirm(type)
  }

  const handleStampConfirmOk = async () => {
    if (!stampConfirm || isStampConfirmSubmitting) return
    setIsStampConfirmSubmitting(true)
    // API 完了までダイアログを開いたままにし、その間 OK を disabled にする（重複送信防止）。
    // onStampClick が将来 throw しても finally でダイアログを閉じ、固着を防ぐ
    try {
      await onStampClick(stampConfirm)
    } finally {
      setStampConfirm(null)
    }
  }

  const buildStampConfirmTitle = (type: StampType): string => {
    const label = STAMP_LABELS[type]
    const currentValue = stamps[type]
    if (currentValue) {
      return `すでに${label}が記録されていますが、現在時刻で更新しますか？`
    }
    return `${label}しますか？`
  }

  // 打刻 / 並び替え等の送信中は多重実行を防ぐため打刻ボタンを非活性化する
  const isStampButtonDisabled = isCompleted || isStamping || isMutatingVisit

  return (
    <>
      <div className={styles.card}>
        <div className={styles.deliveryLabelRow}>
          <span className={styles.optionalBadge}>任意</span>
          <span className={styles.deliveryLabelText}>納品先を選択または入力</span>
        </div>
        <div className={styles.deliveryRow}>
          <ComboBox
            options={deliveryDestinationOptions}
            value={deliveryTenantLabel}
            placeholder={DELIVERY_DESTINATION_PLACEHOLDER}
            onChange={onDeliveryTenantChange}
            disabled={isCompleted}
            maxLength={DELIVERY_DESTINATION_MAX_LENGTH}
            hideSearchIcon
            firstGroupValues={deliveryHistoryValues}
            // 視覚ラベル（.deliveryLabelText）は span で input と関連付けられないため、
            // スクリーンリーダー向けのアクセシブルネームを明示する
            ariaLabel="納品先を選択または入力"
          />
          <button
            type="button"
            className={styles.selectButton}
            onClick={onSelectDelivery}
            disabled={isCompleted || isMutatingVisit || isStamping}
          >
            決定
          </button>
        </div>

        {savedDeliverySource !== null && (
          <div className={styles.savedDeliveryRow}>
            {/* マスタ選択だが候補一覧に該当テナントが無く名称を解決できない場合のフォールバック。
                行ごと消すとクリアボタンも出なくなり解除不能になるため、必ず行を表示する。
                手入力／マスタで表示上の区別（ラベル）は付けない */}
            <span className={styles.savedDeliveryText}>
              {savedDeliveryLabel || '（納品先名を取得できませんでした）'}
            </span>
            {!isCompleted && (
              <button
                type="button"
                className={styles.savedDeliveryClearButton}
                onClick={onClearDelivery}
                disabled={isMutatingVisit || isStamping}
                aria-label="納品先の紐付けを解除"
              >
                <XCircle size={24} color="var(--color-sp-delivery-chip-text)" aria-hidden={true} />
              </button>
            )}
          </div>
        )}

        <div className={styles.tabContainer}>
          <div className={styles.tabRow}>
            <button
              className={`${styles.tab} ${styles.tabStamp} ${activeTab === 'stamp' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('stamp')}
            >
              {!isCompleted && <span className={styles.stampBadge}>{visitNumber}</span>}
              打刻
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'detail' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('detail')}
            >
              作業詳細
            </button>
          </div>

          {activeTab === 'stamp' ? (
            <div className={styles.stampArea}>
              <div className={styles.stampRow}>
                <button
                  className={`${styles.stampButton} ${isStampButtonDisabled ? styles.stampButtonDisabled : ''}`}
                  onClick={() => handleStampButtonClick('checkin')}
                  disabled={isStampButtonDisabled}
                >
                  入場
                </button>
                {stamps.checkin && (
                  <span className={styles.stampDateTime}>{formatStampTime(stamps.checkin)}</span>
                )}
              </div>
              <div className={styles.stampRow}>
                <button
                  className={`${styles.stampButton} ${isStampButtonDisabled ? styles.stampButtonDisabled : ''}`}
                  onClick={() => handleStampButtonClick('start')}
                  disabled={isStampButtonDisabled}
                >
                  作業開始
                </button>
                {stamps.start && (
                  <span className={styles.stampDateTime}>{formatStampTime(stamps.start)}</span>
                )}
              </div>
              <div className={styles.stampRow}>
                <button
                  className={`${styles.stampButton} ${isStampButtonDisabled ? styles.stampButtonDisabled : ''}`}
                  onClick={() => handleStampButtonClick('end')}
                  disabled={isStampButtonDisabled}
                >
                  作業完了
                </button>
                {stamps.end && (
                  <span className={styles.stampDateTime}>{formatStampTime(stamps.end)}</span>
                )}
              </div>
              <div className={styles.stampRow}>
                <button
                  className={`${styles.stampButton} ${isStampButtonDisabled ? styles.stampButtonDisabled : ''}`}
                  onClick={() => handleStampButtonClick('checkout')}
                  disabled={isStampButtonDisabled}
                >
                  退場
                </button>
                {stamps.checkout && (
                  <span className={styles.stampDateTime}>{formatStampTime(stamps.checkout)}</span>
                )}
              </div>
            </div>
          ) : (
            <div className={styles.detailArea}>
              <DeliveryWorkTypeChips
                types={workTypeSettings}
                selectedIds={selectedIds}
                onToggle={toggleId}
                disabled={isInputDisabled}
              />

              <div className={styles.detailTextareaWrapper}>
                <label htmlFor={`memo-${visitId}`} className={styles.detailMemoLabel}>
                  メモ（任意）
                </label>
                <textarea
                  id={`memo-${visitId}`}
                  ref={textareaRef}
                  className={`${styles.detailTextarea} ${isInputDisabled ? styles.detailTextareaDisabled : ''}`}
                  placeholder="補足があれば入力"
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  maxLength={500}
                  disabled={isInputDisabled}
                />
              </div>

              {(existingAttachments.length > 0 || newFiles.length > 0) && (
                <div className={styles.detailFileList}>
                  {/* 既存（保存済み）添付: プレビュー対応は確認用ページへの静的リンク（別タブ）、
                      非対応（xls/xlsx）は直接ダウンロード。
                      dbVisitId は existingAttachments が存在する時点で必ず数値（保存済み visit） */}
                  {dbVisitId !== null &&
                    existingAttachments.map((attachment) => (
                      <div key={`existing-${attachment.id}`} className={styles.detailFileItem}>
                        {/* プレビュー対応（png/jpg/jpeg/pdf）は確認用ページへ。
                            非対応（xls/xlsx）はプレビューせず直接ダウンロードする */}
                        {canPreviewFileAttachment(attachment.name) ? (
                          <A
                            href={buildAttachmentPreviewUrl({
                              deliveryReportUid,
                              shipmentUid,
                              visitId: dbVisitId,
                              attachmentId: attachment.id,
                              name: attachment.name,
                            })}
                            newTab
                            title={attachment.name}
                            className={styles.detailFileName}
                          >
                            {attachment.name}
                          </A>
                        ) : (
                          <button
                            type="button"
                            className={styles.detailFileName}
                            title={attachment.name}
                            onClick={() => handleDownloadAttachment(attachment.id, attachment.name)}
                          >
                            {attachment.name}
                          </button>
                        )}
                        <button
                          className={styles.detailFileDeleteButton}
                          onClick={() => handleRemoveExistingAttachment(attachment.id)}
                          aria-label="ファイルを削除"
                          disabled={isInputDisabled}
                        >
                          <Trash2 size={16} aria-hidden={true} />
                        </button>
                      </div>
                    ))}
                  {/* 新規（未保存）ファイルは確認用ページが存在しないためプレーン表示 */}
                  {newFiles.map((file, index) => (
                    <div key={`new-${index}`} className={styles.detailFileItem}>
                      <span className={styles.detailFileNameStatic} title={file.name}>
                        {file.name}
                      </span>
                      <button
                        className={styles.detailFileDeleteButton}
                        onClick={() => handleRemoveNewFile(index)}
                        aria-label="ファイルを削除"
                        disabled={isInputDisabled}
                      >
                        <Trash2 size={16} aria-hidden={true} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className={styles.detailFileAddRow}>
                <button
                  className={styles.detailFileAddButton}
                  onClick={handleFileAdd}
                  disabled={isInputDisabled}
                >
                  <Paperclip size={16} aria-hidden={true} />
                  添付を追加
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept={ACCEPT_ATTR}
                  onChange={handleFileChange}
                />
              </div>

              <div className={styles.detailSaveWrapper}>
                <button
                  className={`${styles.detailSaveButton} ${isInputDisabled ? styles.detailSaveButtonDisabled : ''}`}
                  disabled={isInputDisabled}
                  onClick={() => setShowSaveConfirm(true)}
                >
                  保存
                </button>
              </div>
            </div>
          )}
        </div>

        {showControls && (
          <div className={styles.controlsRow}>
            <button
              className={`${styles.controlButton} ${styles.controlButtonDelete}`}
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isMutatingVisit || isStamping}
              aria-label="削除"
            >
              <Trash2 size={16} aria-hidden={true} />
            </button>
            <div className={styles.controlArrows}>
              <button
                className={styles.controlButton}
                onClick={onMoveUp}
                disabled={!canMoveUp || isMutatingVisit || isStamping}
                aria-label="上へ移動"
              >
                <ArrowUp size={24} aria-hidden={true} />
              </button>
              <button
                className={styles.controlButton}
                onClick={onMoveDown}
                disabled={!canMoveDown || isMutatingVisit || isStamping}
                aria-label="下へ移動"
              >
                <ArrowDown size={24} aria-hidden={true} />
              </button>
            </div>
          </div>
        )}
      </div>

      <SpConfirmModal
        isOpen={showDeleteConfirm}
        title="この納品先を削除しますか？"
        variant="danger"
        onOk={() => {
          setShowDeleteConfirm(false)
          onDelete()
        }}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      <SpConfirmModal
        isOpen={showSaveConfirm}
        title="作業内容を保存しますか？"
        variant="primary"
        onOk={() => {
          setShowSaveConfirm(false)
          handleSave()
        }}
        onCancel={() => setShowSaveConfirm(false)}
      />

      <SpConfirmModal
        isOpen={stampConfirm !== null}
        title={stampConfirm !== null ? buildStampConfirmTitle(stampConfirm) : ''}
        okDisabled={isStampConfirmSubmitting}
        onOk={handleStampConfirmOk}
        // 送信中はオーバーレイ/キャンセルでの close を抑止（打刻が走っているため）
        onCancel={() => {
          if (isStampConfirmSubmitting) return
          setStampConfirm(null)
        }}
      />
    </>
  )
}
